import React, { useMemo, useState } from 'react';
import { CheckPicker, DateRangePicker, Message, SelectPicker } from 'rsuite';
import { FilterField, FilterToolbar } from '@nsi-advisory-services/components';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import { showToast } from '../utils/toast';
import { formatCurrency } from '../utils/currency';
import { getQuickDateRanges } from '../utils/dateRanges';
import {
  useTimesheetConfig,
  useTimesheetEmployees,
  useTimesheetReport,
  useTimesheetReportExport,
  useTimesheetTasks,
} from '../hooks/useTimesheet';
import { Metric, ProviderBanner, TimesheetNav } from '../components/timesheet/TimesheetParts';
import {
  addDays,
  dateFromIso,
  downloadCsv,
  formatDate,
  formatHours,
  isoFromDate,
  periodFor,
  previousPeriod,
  todayIso,
} from '../utils/timesheet';
import './Timesheet.css';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const GROUP_OPTIONS = [
  { value: 'project', label: 'Project' },
  { value: 'task', label: 'Task' },
  { value: 'client', label: 'Client' },
  { value: 'employee', label: 'Employee' },
  { value: 'billable', label: 'Billable / non-billable' },
  { value: 'rd', label: 'R&D credit / not' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const GROUP_LABEL = Object.fromEntries(GROUP_OPTIONS.map((o) => [o.value, o.label]));

// Validated pair (dataviz validator: lightness, chroma, CVD and contrast all
// pass on white). Billable is the first slot; the legend carries identity.
const SERIES = { billable: '#2a78d6', nonBillable: '#eb6834' };

const pct = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

// Cost, projected revenue and margin cells. The server only sends
// `financials` to payroll and admins, so their absence is the whole check.
// Hours left out of a figure for want of a rate are shown under it.
function MoneyCells({ f }) {
  if (!f) return null;
  const missing = (hours) => (hours > 0 ? <div className="nsi-table__muted small">{formatHours(hours)} h no rate</div> : null);
  return (
    <>
      <td className="nsi-table__right bdc-num">
        {f.costedHours > 0 ? formatCurrency(f.cost) : <span className="nsi-table__muted">No rate</span>}
        {f.costedHours > 0 ? missing(f.noCostHours) : null}
      </td>
      <td className="nsi-table__right bdc-num">
        {f.revenueHours > 0 ? formatCurrency(f.revenue) : <span className="nsi-table__muted">No rate</span>}
        {f.revenueHours > 0 ? missing(f.noRevenueHours) : null}
      </td>
      <td className={`nsi-table__right bdc-num${f.margin < 0 ? ' bdc-text-bad' : ''}`}>{formatCurrency(f.margin)}</td>
    </>
  );
}

// Daily totals rolled up to weeks or months once a day per column would be
// too thin to read.
const bucketDaily = (daily, start, end) => {
  const span = (dateFromIso(end) - dateFromIso(start)) / 86400000 + 1;
  const unit = span <= 62 ? 'day' : span <= 370 ? 'week' : 'month';
  const keyOf = (iso) => {
    if (unit === 'day') return iso;
    if (unit === 'month') return iso.slice(0, 7);
    const d = dateFromIso(iso);
    d.setDate(d.getDate() - d.getDay());
    return isoFromDate(d);
  };
  const map = new Map();
  if (unit === 'day') {
    for (let cur = start; cur <= end; cur = addDays(cur, 1)) map.set(cur, { key: cur, billable: 0, nonBillable: 0 });
  }
  daily.forEach((d) => {
    const key = keyOf(d.date);
    const row = map.get(key) || { key, billable: 0, nonBillable: 0 };
    row.billable += d.billableHours;
    row.nonBillable += d.hours - d.billableHours;
    map.set(key, row);
  });
  const rows = [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  const label = (key) => {
    if (unit === 'month') return dateFromIso(`${key}-01`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    if (unit === 'week') return `Wk ${formatDate(key)}`;
    return formatDate(key);
  };
  return { unit, rows: rows.map((r) => ({ ...r, label: label(r.key) })) };
};

/**
 * Timesheet reports: hours for any range, grouped and sub-grouped any way,
 * filtered by people, projects and billability, exportable as CSV.
 */
export default function TimesheetReportsPage() {
  const current = useMemo(() => periodFor(todayIso()), []);
  const [range, setRange] = useState([dateFromIso(current.start), dateFromIso(current.end)]);
  const [groupBy, setGroupBy] = useState('project');
  const [thenBy, setThenBy] = useState(null);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [taskIds, setTaskIds] = useState([]);
  const [billable, setBillable] = useState('all');
  const [rd, setRd] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());

  const configQuery = useTimesheetConfig();
  const config = configQuery.data;
  const installed = config?.installed !== false;
  const employeesQuery = useTimesheetEmployees({ enabled: installed });
  const tasksQuery = useTimesheetTasks({ includeArchived: true }, { enabled: installed });
  const exportMutation = useTimesheetReportExport();

  const start = range?.[0] ? isoFromDate(range[0]) : null;
  const end = range?.[1] ? isoFromDate(range[1]) : null;
  const params = {
    start,
    end,
    groupBy,
    thenBy: thenBy || undefined,
    employeeIds,
    taskIds,
    billable: billable === 'all' ? undefined : billable,
    rd: rd === 'all' ? undefined : rd,
  };
  const reportQuery = useTimesheetReport(params, { enabled: installed });
  const report = reportQuery.data;

  const employees = useMemo(() => employeesQuery.data?.employees || [], [employeesQuery.data]);
  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: e.id, label: e.isActive ? e.name : `${e.name} (inactive)` })),
    [employees]
  );
  const projectOptions = useMemo(
    () => (tasksQuery.data?.tasks || []).map((t) => ({
      value: t.id,
      label: t.depth === 0 ? t.name : t.pathNames.slice(1).join(' / '),
      project: t.pathNames[0],
    })),
    [tasksQuery.data]
  );

  const quickRanges = useMemo(() => {
    const prev = previousPeriod(current);
    return [
      { label: 'This Pay Period', value: [dateFromIso(current.start), dateFromIso(current.end)] },
      { label: 'Last Pay Period', value: [dateFromIso(prev.start), dateFromIso(prev.end)] },
      ...getQuickDateRanges(),
    ];
  }, [current]);

  const chart = useMemo(() => (report ? bucketDaily(report.daily || [], report.start, report.end) : null), [report]);
  const chartData = useMemo(() => (chart ? {
    labels: chart.rows.map((r) => r.label),
    datasets: [
      {
        label: 'Billable',
        data: chart.rows.map((r) => Math.round(r.billable * 100) / 100),
        backgroundColor: SERIES.billable,
        maxBarThickness: 24,
        borderRadius: 0,
        stack: 'hours',
      },
      {
        label: 'Non-billable',
        data: chart.rows.map((r) => Math.round(r.nonBillable * 100) / 100),
        backgroundColor: SERIES.nonBillable,
        maxBarThickness: 24,
        // The data end is rounded; the join with the segment below is square,
        // and the white border is the 2px gap between the two.
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: 'bottom',
        borderColor: '#ffffff',
        borderWidth: { bottom: 2 },
        stack: 'hours',
      },
    ],
  } : null), [chart]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, color: '#45474c' } },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatHours(ctx.parsed.y)} h`,
          footer: (items) => `Total: ${formatHours(items.reduce((s, i) => s + i.parsed.y, 0))} h`,
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: '#75777d', maxRotation: 0, autoSkip: true } },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: '#eceef0' },
        border: { display: false },
        ticks: { color: '#75777d', callback: (v) => `${v} h` },
      },
    },
  }), []);

  const groups = report?.groups || [];
  const totalHours = report?.totals?.hours || 0;
  const showBudget = groupBy === 'project' || groupBy === 'task';
  const fin = report?.financials || null;
  const columnCount = 8 + (showBudget ? 1 : 0) + (fin ? 3 : 0);

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const exportSummary = () => {
    if (!report) return;
    const money = (f) => (fin ? [f?.cost ?? '', f?.revenue ?? '', f?.margin ?? '', f?.noCostHours ?? '', f?.noRevenueHours ?? ''] : []);
    const header = [GROUP_LABEL[groupBy], ...(thenBy ? [GROUP_LABEL[thenBy]] : []), 'Hours', 'Billable hours', 'Billable %', 'R&D hours', 'Entries', 'People',
      ...(fin ? ['Cost', 'Projected revenue', 'Margin', 'Hours with no pay rate', 'Hours with no ProPlan rate'] : [])];
    const rows = [header];
    groups.forEach((g) => {
      rows.push([g.label, ...(thenBy ? [''] : []), g.hours, g.billableHours, pct(g.billableHours, g.hours), g.rdHours, g.entries, g.employees, ...money(g.financials)]);
      (g.children || []).forEach((c) => {
        rows.push([g.label, c.label, c.hours, c.billableHours, pct(c.billableHours, c.hours), c.rdHours, c.entries, c.employees, ...money(c.financials)]);
      });
    });
    downloadCsv(`timesheet-summary-${report.start}-to-${report.end}.csv`, rows);
  };

  const exportEntries = async () => {
    try {
      const data = await exportMutation.mutateAsync(params);
      const withMoney = Boolean(data.financials);
      const rows = [['Date', 'Employee', 'Client', 'Project', 'Task', 'Description', 'Start', 'End', 'Hours', 'Billable', 'R&D credit', 'Source',
        ...(withMoney ? ['Pay rate', 'Cost', 'ProPlan rate', 'Projected revenue'] : [])]];
      (data.entries || []).forEach((e) => {
        rows.push([e.date, e.employeeName, e.clientName || '', e.projectName || '', e.taskPath, e.description,
          e.startTime || '', e.endTime || '', e.hours, e.billable ? 'Yes' : 'No', e.rdEligible ? 'Yes' : 'No', e.source,
          ...(withMoney ? [e.costRate ?? 'No rate', e.cost ?? '', e.revenueRate ?? 'No rate', e.revenue ?? ''] : [])]);
      });
      downloadCsv(`timesheet-entries-${data.start}-to-${data.end}.csv`, rows);
      if (data.truncated) showToast('warning', 'The export was capped at 20,000 entries. Narrow the range to get the rest.');
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  return (
    <div className="bdc-page ts-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Timesheet', to: '/timesheet' }, { label: 'Reports' }]}
        icon="bi-bar-chart-line"
        title="Timesheet Reports"
        showBack={false}
        actions={(
          <>
            <button type="button" className="bdc-btn" onClick={exportSummary} disabled={!report || groups.length === 0}>
              <i className="bi bi-filetype-csv" aria-hidden="true" />
              Export summary
            </button>
            <button type="button" className="bdc-btn" onClick={exportEntries} disabled={!report || exportMutation.isPending}>
              <i className="bi bi-download" aria-hidden="true" />
              Export entries
            </button>
            <TimesheetNav config={config} />
          </>
        )}
      />

      <ProviderBanner config={config} />

      <FilterToolbar>
        <FilterField label="Dates" basis={240} max={300}>
          <DateRangePicker
            value={range}
            onChange={(value) => setRange(value || [dateFromIso(current.start), dateFromIso(current.end)])}
            format="MM/dd/yyyy"
            character=" – "
            isoWeek={false}
            weekStart={0}
            ranges={quickRanges}
            cleanable={false}
            preventOverflow
            block
          />
        </FilterField>
        <FilterField label="Group by" basis={170} max={220}>
          <SelectPicker
            data={GROUP_OPTIONS}
            value={groupBy}
            onChange={(value) => {
              setGroupBy(value || 'project');
              if (value === thenBy) setThenBy(null);
              setExpanded(new Set());
            }}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
        <FilterField label="Then by" basis={170} max={220}>
          <SelectPicker
            data={GROUP_OPTIONS.filter((o) => o.value !== groupBy)}
            value={thenBy}
            onChange={(value) => {
              setThenBy(value || null);
              setExpanded(new Set());
            }}
            placeholder="Nothing"
            searchable={false}
            block
          />
        </FilterField>
        {/* Whoever the server lets this person see: everyone for a reporter,
            themselves and the people they manage for a coach, or just
            themselves - in which case the field is locked to them. */}
        {employees.length > 1 ? (
          <FilterField label="People" basis={200} max={280}>
            <CheckPicker
              data={employeeOptions}
              countable={false}
              renderValue={renderEmployeeSelection(employeeOptions)}
              value={employeeIds}
              onChange={(value) => setEmployeeIds(value || [])}
              placeholder={config?.permissions?.canReportAll ? 'Everyone' : 'You and the people you manage'}
              preventOverflow
              block
            />
          </FilterField>
        ) : employees.length === 1 ? (
          <FilterField label="People" basis={200} max={280}>
            <CheckPicker
              data={employeeOptions}
              value={[employees[0].id]}
              cleanable={false}
              disabled
              block
              title="These reports cover your own time."
            />
          </FilterField>
        ) : null}
        <FilterField label="Projects / tasks" basis={220} max={300}>
          <CheckPicker
            data={projectOptions}
            groupBy="project"
            value={taskIds}
            onChange={(value) => setTaskIds(value || [])}
            placeholder="All projects"
            countable
            preventOverflow
            block
          />
        </FilterField>
        <FilterField label="Billable">
          <div className="bdc-segment" role="group" aria-label="Billable filter">
            {[['all', 'All'], ['billable', 'Billable'], ['nonbillable', 'Non-billable']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`bdc-btn${billable === key ? ' bdc-btn--primary' : ''}`}
                aria-pressed={billable === key}
                onClick={() => setBillable(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </FilterField>
        <FilterField label="R&amp;D credit">
          <div className="bdc-segment" role="group" aria-label="R&D credit filter">
            {[['all', 'All'], ['rd', 'Eligible'], ['nonrd', 'Not R&D']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`bdc-btn${rd === key ? ' bdc-btn--primary' : ''}`}
                aria-pressed={rd === key}
                onClick={() => setRd(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </FilterField>
      </FilterToolbar>

      {reportQuery.error ? <Message type="error" showIcon>{String(reportQuery.error?.message || reportQuery.error)}</Message> : null}
      {report?.truncated ? (
        <Message type="warning" showIcon>This range has more than 20,000 entries; only the first 20,000 are counted. Narrow the range.</Message>
      ) : null}

      <section className="bdc-metrics">
        <Metric label="Total hours" value={report ? formatHours(totalHours) : null} foot={report ? `${formatDate(report.start)} – ${formatDate(report.end)}` : null} />
        <Metric label="Billable hours" value={report ? formatHours(report.totals.billableHours) : null} foot={report ? `${pct(report.totals.billableHours, totalHours)} billable` : null} />
        <Metric label="R&amp;D credit hours" value={report ? formatHours(report.totals.rdHours) : null} foot={report ? `${pct(report.totals.rdHours, totalHours)} of hours` : null} />
        <Metric label="Entries" value={report ? report.totals.entries.toLocaleString('en-US') : null} />
        <Metric label="People / projects" value={report ? `${report.totals.employees} / ${report.totals.projects}` : null} />
        {fin ? (
          <>
            <Metric label="Cost to company" value={formatCurrency(fin.cost)} foot={`${formatHours(fin.costedHours)} h at EmployeeConnect pay rates`} />
            <Metric label="Projected revenue" value={formatCurrency(fin.revenue)} foot={`${formatHours(fin.revenueHours)} h at ProPlan rates`} />
            <Metric
              label="Margin"
              value={formatCurrency(fin.margin)}
              foot={fin.revenue > 0 ? `${Math.round((fin.margin / fin.revenue) * 100)}% of revenue` : null}
              footTone={fin.margin < 0 ? 'bad' : null}
            />
          </>
        ) : null}
      </section>

      {fin && !fin.revenueStatus?.available ? (
        <Message type="warning" showIcon>{fin.revenueStatus.message}</Message>
      ) : null}
      {fin && (fin.noCostHours > 0 || fin.noRevenueHours > 0) ? (
        <Message type="info" showIcon>
          Some hours have no rate and are left out of the money totals, so read them as low:
          {fin.noCostHours > 0 ? ` ${formatHours(fin.noCostHours)} h have no pay rate in force (cost).` : ''}
          {fin.noRevenueHours > 0
            ? ` ${formatHours(fin.noRevenueHours)} h have no projected revenue (${(fin.noRevenueBreakdown || []).map((b) => `${b.label}: ${formatHours(b.hours)} h`).join('; ')}).`
            : ''}
        </Message>
      ) : null}

      <Section title="Hours over time" note={chart ? `by ${chart.unit}` : null}>
        <div className="ts-chart">
          {chartData && report?.totals?.entries ? (
            <Bar data={chartData} options={chartOptions} aria-label="Billable and non-billable hours over time" role="img" />
          ) : (
            <div className="ts-empty h-100 justify-content-center">
              <i className="bi bi-bar-chart" aria-hidden="true" />
              {reportQuery.isLoading ? 'Loading…' : 'No time logged in this range.'}
            </div>
          )}
        </div>
      </Section>

      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">By {GROUP_LABEL[groupBy].toLowerCase()}{thenBy ? `, then ${GROUP_LABEL[thenBy].toLowerCase()}` : ''}</h3>
            <span className="bdc-count">{groups.length}</span>
          </div>
          {thenBy && groups.length ? (
            <div className="bdc-table-head__actions">
              <button
                type="button"
                className="bdc-btn bdc-btn--sm"
                onClick={() => setExpanded(expanded.size === groups.length ? new Set() : new Set(groups.map((g) => g.key)))}
              >
                {expanded.size === groups.length ? 'Collapse all' : 'Expand all'}
              </button>
            </div>
          ) : null}
        </div>
        <table className="nsi-table">
          <thead>
            <tr>
              <th>{GROUP_LABEL[groupBy]}</th>
              <th className="nsi-table__right">Hours</th>
              <th className="nsi-table__right">Billable</th>
              <th className="nsi-table__right">Billable %</th>
              <th className="nsi-table__right">R&amp;D</th>
              <th className="nsi-table__right">Share</th>
              <th className="nsi-table__right">Entries</th>
              <th className="nsi-table__right">People</th>
              {fin ? (
                <>
                  <th className="nsi-table__right">Cost</th>
                  <th className="nsi-table__right">Revenue</th>
                  <th className="nsi-table__right">Margin</th>
                </>
              ) : null}
              {showBudget ? <th>Budget (all time)</th> : null}
            </tr>
          </thead>
          <tbody>
            {reportQuery.isLoading ? (
              <tr><td colSpan={columnCount} className="nsi-table__empty">Loading…</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={columnCount} className="nsi-table__empty">No time logged in this range.</td></tr>
            ) : groups.map((g) => {
              const isOpen = expanded.has(g.key);
              const over = g.budgetHours != null && g.allTimeHours > g.budgetHours;
              return (
                <React.Fragment key={g.key}>
                  <tr>
                    <td>
                      {thenBy ? (
                        <button type="button" className="ts-expander" aria-expanded={isOpen} onClick={() => toggle(g.key)}>
                          <i className="bi bi-chevron-right" aria-hidden="true" />
                          <span className="fw-semibold">{g.label}</span>
                        </button>
                      ) : <span className="fw-semibold">{g.label}</span>}
                      {g.code || g.clientName ? (
                        <div className="nsi-table__muted small">{[g.code, g.clientName].filter(Boolean).join(' · ')}</div>
                      ) : null}
                    </td>
                    <td className="nsi-table__right bdc-num fw-semibold">{formatHours(g.hours)}</td>
                    <td className="nsi-table__right bdc-num">{formatHours(g.billableHours)}</td>
                    <td className="nsi-table__right bdc-num">{pct(g.billableHours, g.hours)}</td>
                    <td className="nsi-table__right bdc-num">{formatHours(g.rdHours)}</td>
                    <td className="nsi-table__right bdc-num">{pct(g.hours, totalHours)}</td>
                    <td className="nsi-table__right bdc-num">{g.entries}</td>
                    <td className="nsi-table__right bdc-num">{g.employees}</td>
                    <MoneyCells f={g.financials} />
                    {showBudget ? (
                      <td>
                        {g.budgetHours != null ? (
                          <div className="ts-budget">
                            <div className="ts-budget__track">
                              <div
                                className={`ts-budget__fill${over ? ' ts-budget__fill--over' : ''}`}
                                style={{ width: `${Math.min(100, g.budgetHours > 0 ? (g.allTimeHours / g.budgetHours) * 100 : 100)}%` }}
                              />
                            </div>
                            <span className={`ts-budget__label${over ? ' bdc-text-bad' : ''}`}>
                              {formatHours(g.allTimeHours)} of {formatHours(g.budgetHours)} h{over ? ' — over budget' : ''}
                            </span>
                          </div>
                        ) : <span className="nsi-table__muted">—</span>}
                      </td>
                    ) : null}
                  </tr>
                  {isOpen ? (g.children || []).map((c) => (
                    <tr key={`${g.key}|${c.key}`} className="ts-child-row">
                      <td>{c.label}</td>
                      <td className="nsi-table__right bdc-num">{formatHours(c.hours)}</td>
                      <td className="nsi-table__right bdc-num">{formatHours(c.billableHours)}</td>
                      <td className="nsi-table__right bdc-num">{pct(c.billableHours, c.hours)}</td>
                      <td className="nsi-table__right bdc-num">{formatHours(c.rdHours)}</td>
                      <td className="nsi-table__right bdc-num">{pct(c.hours, g.hours)}</td>
                      <td className="nsi-table__right bdc-num">{c.entries}</td>
                      <td className="nsi-table__right bdc-num">{c.employees}</td>
                      <MoneyCells f={c.financials} />
                      {showBudget ? <td /> : null}
                    </tr>
                  )) : null}
                </React.Fragment>
              );
            })}
          </tbody>
          {groups.length ? (
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="nsi-table__right bdc-num">{formatHours(totalHours)}</td>
                <td className="nsi-table__right bdc-num">{formatHours(report.totals.billableHours)}</td>
                <td className="nsi-table__right bdc-num">{pct(report.totals.billableHours, totalHours)}</td>
                <td className="nsi-table__right bdc-num">{formatHours(report.totals.rdHours)}</td>
                <td className="nsi-table__right bdc-num">100%</td>
                <td className="nsi-table__right bdc-num">{report.totals.entries}</td>
                <td className="nsi-table__right bdc-num">{report.totals.employees}</td>
                <MoneyCells f={fin} />
                {showBudget ? <td /> : null}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
